/**
 * Route table for the CoordioCortex web app.
 *
 * This file is deliberately separate from `src/utils/routes.ts`: that file owns the path
 * *constants*, this one owns what each path actually *renders*. Keeping the two apart makes a
 * `<Navigate>` redirect stub visibly distinct from a real page, which is what E2E route
 * verification needs before a test is written against a path.
 *
 * App.tsx owns the provider shell (router, auth, query client, SignalR, layout); everything
 * below runs inside it. Route order is significant — react-router matches in declaration
 * order, so static segments stay ahead of the parameterised paths they would otherwise shadow.
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from '../components/common';

import { DashboardPage } from '../pages/DashboardPage';
import { DashboardCustomizationPage } from '../pages/DashboardCustomizationPage';
import { ProjectListPage } from '../pages/ProjectListPage';
import { ApiKeyListPage } from '../pages/ApiKeyListPage';
import { AgentRegistryPage } from '../pages/AgentRegistryPage';
import { AgentDetailPage } from '../pages/AgentDetailPage';
import { AgentForm } from '../components/agents/AgentForm';
import { SessionListPage } from '../pages/SessionListPage';
import { SessionDetailPage } from '../pages/SessionDetailPage';
import { MemoryListPage } from '../pages/MemoryListPage';
import { MemoryDetailPage } from '../pages/MemoryDetailPage';
import { MemoryDefinitionsPage } from '../pages/MemoryDefinitionsPage';
import { MemoryDefinitionEditorPage } from '../pages/MemoryDefinitionEditorPage';
import { MemoryPolicyPage } from '../pages/MemoryPolicyPage';
import { MemoryRulesPage } from '../pages/MemoryRulesPage';
import { MemoryPipelinePage } from '../pages/MemoryPipelinePage';
import { MemoryQuarantinePage } from '../pages/MemoryQuarantinePage';
import { SkillsPage } from '../pages/SkillsPage';
import { ToolsListPage } from '../pages/ToolsListPage';
import { ToolDetailPage } from '../pages/ToolDetailPage';
import { ToolForm } from '../components/tools/ToolForm';
import { WorkflowListPage } from '../pages/WorkflowListPage';
import { WorkflowDetailPage } from '../pages/WorkflowDetailPage';
import { WorkflowFormPage } from '../pages/WorkflowFormPage';
import { AgentComposerPage } from '../pages/AgentComposerPage';
import { ScheduleListPage } from '../pages/ScheduleListPage';
import { ScheduleDetailPage } from '../pages/ScheduleDetailPage';
import { ScheduleForm } from '../components/schedules/ScheduleForm';
import { InvestigationListPage } from '../pages/InvestigationListPage';
import { InvestigationDetailPage } from '../pages/InvestigationDetailPage';
import { InvestigationTemplatesListPage } from '../pages/InvestigationTemplatesListPage';
import { InvestigationForm } from '../components/investigations/InvestigationForm';
import { InvestigationTemplateForm } from '../components/investigations/InvestigationTemplateForm';
import { SafetyGatesListPage } from '../pages/SafetyGatesListPage';
import { ApprovalQueuePage } from '../pages/ApprovalQueuePage';
import { GuardrailsPage } from '../pages/GuardrailsPage';
import { GuardrailBindingsPage } from '../pages/GuardrailBindingsPage';
import { GuardrailExecutionsPage } from '../pages/GuardrailExecutionsPage';
import { SafetyGateForm } from '../components/safetyGates/SafetyGateForm';
import { McpServersListPage } from '../pages/McpServersListPage';
import { McpServerDetailPage } from '../pages/McpServerDetailPage';
import { McpServerForm } from '../components/mcpServers/McpServerForm';
import { ExternalSystemsListPage } from '../pages/ExternalSystemsListPage';
import { ExternalSystemDetailPage } from '../pages/ExternalSystemDetailPage';
import { ExternalSystemForm } from '../components/externalSystems/ExternalSystemForm';
import { WebAutomationSitesListPage } from '../pages/WebAutomationSitesListPage';
import { WebAutomationSiteDetailPage } from '../pages/WebAutomationSiteDetailPage';
import { WebAutomationSiteForm } from '../components/webAutomation/WebAutomationSiteForm';
import { WebAutomationRunsPage } from '../pages/WebAutomationRunsPage';
import { TranscriptionPlaygroundPage } from '../pages/TranscriptionPlaygroundPage';
import { TranscriptionsPage } from '../pages/TranscriptionsPage';
import { TranscriptionDetailPage } from '../pages/TranscriptionDetailPage';
import { TranscriptionHowToUsePage } from '../pages/TranscriptionHowToUsePage';
import { DeveloperHelpHubPage } from '../pages/DeveloperHelpHubPage';
import { DeveloperHelpRecipePage } from '../pages/DeveloperHelpRecipePage';
import { ConnectorsListPage } from '../pages/ConnectorsListPage';
import { ConnectorDetailPage } from '../pages/ConnectorDetailPage';
import { ConnectorInstanceWizardPage } from '../pages/ConnectorInstanceWizardPage';
import { ConnectorInstanceDetailPage } from '../pages/ConnectorInstanceDetailPage';
import { TaskListPage } from '../pages/TaskListPage';
import { TaskDetailPage } from '../pages/TaskDetailPage';
import { TaskFormPage } from '../pages/TaskFormPage';
import { ExternalTaskSystemForm } from '../components/hitl/ExternalTaskSystemForm';
import { ExternalTaskSystemsListPage } from '../pages/ExternalTaskSystemsListPage';
import { UsageAnalyticsPage } from '../pages/UsageAnalyticsPage';
import { ModelCatalogPage } from '../pages/ModelCatalogPage';
import { LlmProviderConfigPage } from '../pages/LlmProviderConfigPage';
import { UserSettingsPage } from '../pages/UserSettingsPage';
import { TenantSettingsPage } from '../pages/TenantSettingsPage';
import { ProjectEditPage } from '../pages/ProjectEditPage';
import { OrchestratorPage } from '../pages/OrchestratorPage';
import { OrchestratorRunPage } from '../pages/OrchestratorRunPage';
import { AgentFunctionsListPage } from '../pages/AgentFunctionsListPage';
import { AgentFunctionDetailPage } from '../pages/AgentFunctionDetailPage';
import { AgentFunctionExecutePage } from '../pages/AgentFunctionExecutePage';
import { AgentFunctionRequestsListPage } from '../pages/AgentFunctionRequestsListPage';
import { AgentFunctionRequestDetailPage } from '../pages/AgentFunctionRequestDetailPage';
import { AgentFunctionForm } from '../components/agentFunctions/AgentFunctionForm';
import { ApiExplorerPage } from '../pages/ApiExplorerPage';
import { ApiExplorerDetailPage } from '../pages/ApiExplorerDetailPage';
import { NotificationLogPage } from '../pages/NotificationLogPage';
import { EmailTemplatesPage } from '../pages/EmailTemplatesPage';
import { LlmMetricsPage } from '../pages/LlmMetricsPage';
import { ROUTES } from '../utils/routes';
import { callbackPath } from '../config/authConfig';
import { SignInCallbackPage } from '../pages/SignInCallbackPage';

/**
 * Every route below the B2C callback is wrapped in `<ProtectedRoute>`; the callback itself must
 * stay reachable unauthenticated so `SsoAuthHandler` can complete the sign-in redirect.
 */
export function CortexAppRoutes() {
  return (
    <Routes>
      {/* B2C redirect callback – stay here while SsoAuthHandler sets auth, then it navigates to home */}
      <Route path={callbackPath} element={<SignInCallbackPage />} />

      {/* Protected Routes */}
      {/* ── Dashboard ───────────────────── */}
      <Route
        path={ROUTES.HOME}
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.DASHBOARD_CUSTOMIZE}
        element={
          <ProtectedRoute>
            <DashboardCustomizationPage />
          </ProtectedRoute>
        }
      />
      {/* ── Tenancy, projects and API keys ────────────── */}
      {/* Redirect stub — tenancy has no page of its own; deep links land on the dashboard. */}
      <Route
        path={ROUTES.TENANTS}
        element={
          <ProtectedRoute>
            <Navigate to={ROUTES.HOME} replace />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PROJECTS}
        element={
          <ProtectedRoute>
            <ProjectListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PROJECT_EDIT}
        element={
          <ProtectedRoute>
            <ProjectEditPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.API_KEYS}
        element={
          <ProtectedRoute>
            <ApiKeyListPage />
          </ProtectedRoute>
        }
      />
      {/* ── Agent registry and sessions ─────────────── */}
      <Route
        path={ROUTES.AGENTS}
        element={
          <ProtectedRoute>
            <AgentRegistryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.AGENT_NEW}
        element={
          <ProtectedRoute>
            <AgentForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.AGENT_EDIT}
        element={
          <ProtectedRoute>
            <AgentForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.AGENT_DETAIL}
        element={
          <ProtectedRoute>
            <AgentDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.SESSIONS}
        element={
          <ProtectedRoute>
            <SessionListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.SESSION_DETAIL}
        element={
          <ProtectedRoute>
            <SessionDetailPage />
          </ProtectedRoute>
        }
      />
      {/* ── Memory — records, definitions, policy and pipeline ────── */}
      <Route
        path={ROUTES.MEMORIES}
        element={
          <ProtectedRoute>
            <MemoryListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.MEMORY_DETAIL}
        element={
          <ProtectedRoute>
            <MemoryDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.MEMORY_DEFINITIONS}
        element={
          <ProtectedRoute>
            <MemoryDefinitionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.MEMORY_DEFINITION_NEW}
        element={
          <ProtectedRoute>
            <MemoryDefinitionEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.MEMORY_DEFINITION_DETAIL}
        element={
          <ProtectedRoute>
            <MemoryDefinitionEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.MEMORY_POLICY}
        element={
          <ProtectedRoute>
            <MemoryPolicyPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.MEMORY_RULES}
        element={
          <ProtectedRoute>
            <MemoryRulesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.MEMORY_PIPELINE}
        element={
          <ProtectedRoute>
            <MemoryPipelinePage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.MEMORY_QUARANTINE}
        element={
          <ProtectedRoute>
            <MemoryQuarantinePage />
          </ProtectedRoute>
        }
      />
      {/* ── Orchestrator ──────────────────── */}
      <Route
        path={ROUTES.ORCHESTRATOR}
        element={
          <ProtectedRoute>
            <OrchestratorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.ORCHESTRATOR_RUN}
        element={
          <ProtectedRoute>
            <OrchestratorRunPage />
          </ProtectedRoute>
        }
      />
      {/* ── Agent functions and function requests ─────────── */}
      <Route
        path={ROUTES.AGENT_FUNCTIONS}
        element={
          <ProtectedRoute>
            <AgentFunctionsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.AGENT_FUNCTION_NEW}
        element={
          <ProtectedRoute>
            <AgentFunctionForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.AGENT_FUNCTION_EDIT}
        element={
          <ProtectedRoute>
            <AgentFunctionForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.AGENT_FUNCTION_DETAIL}
        element={
          <ProtectedRoute>
            <AgentFunctionDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.AGENT_FUNCTION_EXECUTE}
        element={
          <ProtectedRoute>
            <AgentFunctionExecutePage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.AGENT_FUNCTION_REQUESTS}
        element={
          <ProtectedRoute>
            <AgentFunctionRequestsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.AGENT_FUNCTION_REQUEST_DETAIL}
        element={
          <ProtectedRoute>
            <AgentFunctionRequestDetailPage />
          </ProtectedRoute>
        }
      />
      {/* ── API explorer ──────────────────── */}
      <Route
        path={ROUTES.API_EXPLORER}
        element={
          <ProtectedRoute>
            <ApiExplorerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.API_EXPLORER_DETAIL}
        element={
          <ProtectedRoute>
            <ApiExplorerDetailPage />
          </ProtectedRoute>
        }
      />
      {/* ── Skills and tools ────────────────── */}
      <Route
        path={ROUTES.SKILLS}
        element={
          <ProtectedRoute>
            <SkillsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.TOOLS}
        element={
          <ProtectedRoute>
            <ToolsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.TOOL_NEW}
        element={
          <ProtectedRoute>
            <ToolForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.TOOL_EDIT}
        element={
          <ProtectedRoute>
            <ToolForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.TOOL_DETAIL}
        element={
          <ProtectedRoute>
            <ToolDetailPage />
          </ProtectedRoute>
        }
      />
      {/* ── Workflows and agent composer ────────────── */}
      <Route
        path={ROUTES.WORKFLOWS}
        element={
          <ProtectedRoute>
            <WorkflowListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.WORKFLOW_DETAIL}
        element={
          <ProtectedRoute>
            <WorkflowDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.WORKFLOW_NEW}
        element={
          <ProtectedRoute>
            <WorkflowFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.WORKFLOW_EDIT}
        element={
          <ProtectedRoute>
            <WorkflowFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.COMPOSER}
        element={
          <ProtectedRoute>
            <AgentComposerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.COMPOSER_DETAIL}
        element={
          <ProtectedRoute>
            <AgentComposerPage />
          </ProtectedRoute>
        }
      />
      {/* ── Schedules ───────────────────── */}
      <Route
        path={ROUTES.SCHEDULES}
        element={
          <ProtectedRoute>
            <ScheduleListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.SCHEDULE_NEW}
        element={
          <ProtectedRoute>
            <ScheduleForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.SCHEDULE_EDIT}
        element={
          <ProtectedRoute>
            <ScheduleForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.SCHEDULE_DETAIL}
        element={
          <ProtectedRoute>
            <ScheduleDetailPage />
          </ProtectedRoute>
        }
      />
      {/* ── Investigations and investigation templates ────────── */}
      <Route
        path={ROUTES.INVESTIGATIONS}
        element={
          <ProtectedRoute>
            <InvestigationListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.INVESTIGATION_NEW}
        element={
          <ProtectedRoute>
            <InvestigationForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.INVESTIGATION_EDIT}
        element={
          <ProtectedRoute>
            <InvestigationForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.INVESTIGATION_DETAIL}
        element={
          <ProtectedRoute>
            <InvestigationDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.INVESTIGATION_TEMPLATES}
        element={
          <ProtectedRoute>
            <InvestigationTemplatesListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.INVESTIGATION_TEMPLATE_NEW}
        element={
          <ProtectedRoute>
            <InvestigationTemplateForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.INVESTIGATION_TEMPLATE_EDIT}
        element={
          <ProtectedRoute>
            <InvestigationTemplateForm />
          </ProtectedRoute>
        }
      />
      {/* ── HITL — safety gates and approval queue ────────── */}
      <Route
        path={ROUTES.SAFETY_GATES}
        element={
          <ProtectedRoute>
            <SafetyGatesListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.SAFETY_GATE_NEW}
        element={
          <ProtectedRoute>
            <SafetyGateForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.SAFETY_GATE_EDIT}
        element={
          <ProtectedRoute>
            <SafetyGateForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.APPROVAL_QUEUE}
        element={
          <ProtectedRoute>
            <ApprovalQueuePage />
          </ProtectedRoute>
        }
      />
      {/* ── Guardrails registry / bindings / executions ── */}
      <Route
        path={ROUTES.GUARDRAILS}
        element={
          <ProtectedRoute>
            <GuardrailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.GUARDRAIL_BINDINGS}
        element={
          <ProtectedRoute>
            <GuardrailBindingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.GUARDRAIL_EXECUTIONS}
        element={
          <ProtectedRoute>
            <GuardrailExecutionsPage />
          </ProtectedRoute>
        }
      />
      {/* ── MCP servers ──────────────────── */}
      <Route
        path={ROUTES.MCP_SERVERS}
        element={
          <ProtectedRoute>
            <McpServersListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.MCP_SERVER_NEW}
        element={
          <ProtectedRoute>
            <McpServerForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.MCP_SERVER_EDIT}
        element={
          <ProtectedRoute>
            <McpServerForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.MCP_SERVER_DETAIL}
        element={
          <ProtectedRoute>
            <McpServerDetailPage />
          </ProtectedRoute>
        }
      />
      {/* ── External systems ────────────────── */}
      <Route
        path={ROUTES.EXTERNAL_SYSTEMS}
        element={
          <ProtectedRoute>
            <ExternalSystemsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.EXTERNAL_SYSTEM_DETAIL}
        element={
          <ProtectedRoute>
            <ExternalSystemDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.EXTERNAL_SYSTEM_NEW}
        element={
          <ProtectedRoute>
            <ExternalSystemForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.EXTERNAL_SYSTEM_EDIT}
        element={
          <ProtectedRoute>
            <ExternalSystemForm />
          </ProtectedRoute>
        }
      />
      {/* ── Web automation ─────────────────── */}
      <Route
        path={ROUTES.WEB_AUTOMATION_SITES}
        element={
          <ProtectedRoute>
            <WebAutomationSitesListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.WEB_AUTOMATION_SITE_NEW}
        element={
          <ProtectedRoute>
            <WebAutomationSiteForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.WEB_AUTOMATION_SITE_EDIT}
        element={
          <ProtectedRoute>
            <WebAutomationSiteForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.WEB_AUTOMATION_SITE_DETAIL}
        element={
          <ProtectedRoute>
            <WebAutomationSiteDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.WEB_AUTOMATION_RUNS}
        element={
          <ProtectedRoute>
            <WebAutomationRunsPage />
          </ProtectedRoute>
        }
      />
      {/* ── Transcription ─────────────────── */}
      <Route
        path={ROUTES.TRANSCRIPTION_PLAYGROUND}
        element={
          <ProtectedRoute>
            <TranscriptionPlaygroundPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.TRANSCRIPTIONS}
        element={
          <ProtectedRoute>
            <TranscriptionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.TRANSCRIPTION_DETAIL}
        element={
          <ProtectedRoute>
            <TranscriptionDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.TRANSCRIPTION_HOW_TO_USE}
        element={
          <ProtectedRoute>
            <TranscriptionHowToUsePage />
          </ProtectedRoute>
        }
      />
      {/* ── Developer help ─────────────────── */}
      <Route
        path={ROUTES.DEVELOPER_HELP}
        element={
          <ProtectedRoute>
            <DeveloperHelpHubPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.DEVELOPER_HELP_RECIPE}
        element={
          <ProtectedRoute>
            <DeveloperHelpRecipePage />
          </ProtectedRoute>
        }
      />
      {/* ── Connectors ──────────────────── */}
      <Route
        path={ROUTES.CONNECTORS}
        element={
          <ProtectedRoute>
            <ConnectorsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.CONNECTOR_DETAIL}
        element={
          <ProtectedRoute>
            <ConnectorDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.CONNECTOR_INSTANCE_NEW}
        element={
          <ProtectedRoute>
            <ConnectorInstanceWizardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.CONNECTOR_INSTANCE_DETAIL}
        element={
          <ProtectedRoute>
            <ConnectorInstanceDetailPage />
          </ProtectedRoute>
        }
      />
      {/* ── HITL — tasks and external task systems ────────── */}
      <Route
        path={ROUTES.TASKS}
        element={
          <ProtectedRoute>
            <TaskListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.TASK_DETAIL}
        element={
          <ProtectedRoute>
            <TaskDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.TASK_NEW}
        element={
          <ProtectedRoute>
            <TaskFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.EXTERNAL_TASK_SYSTEMS}
        element={
          <ProtectedRoute>
            {/* Fixed regression: the route used to mount `TaskListPage` (external **tasks**), which
                was a copy-paste from the neighbouring TASKS route and showed users the wrong
                resource. External task **systems** are the registered destinations (Jira,
                ServiceNow, …) that individual tasks queue against; `ExternalTaskSystemsListPage`
                is the CoOrdio list-page for that resource. */}
            <ExternalTaskSystemsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.EXTERNAL_TASK_SYSTEM_NEW}
        element={
          <ProtectedRoute>
            <ExternalTaskSystemForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.EXTERNAL_TASK_SYSTEM_EDIT}
        element={
          <ProtectedRoute>
            <ExternalTaskSystemForm />
          </ProtectedRoute>
        }
      />
      {/* ── AI operations — usage, model catalog and LLM providers ───── */}
      <Route
        path={ROUTES.USAGE_ANALYTICS}
        element={
          <ProtectedRoute>
            <UsageAnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.MODEL_CATALOG}
        element={
          <ProtectedRoute>
            <ModelCatalogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.LLM_PROVIDER_CONFIGS}
        element={
          <ProtectedRoute>
            <LlmProviderConfigPage />
          </ProtectedRoute>
        }
      />
      {/* ── Settings ───────────────────── */}
      <Route
        path={ROUTES.SETTINGS_USER}
        element={
          <ProtectedRoute>
            <UserSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.SETTINGS_TENANT}
        element={
          <ProtectedRoute>
            <TenantSettingsPage />
          </ProtectedRoute>
        }
      />
      {/* ── Notifications, email templates and LLM metrics ──────── */}
      <Route
        path={ROUTES.NOTIFICATIONS}
        element={
          <ProtectedRoute>
            <NotificationLogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.EMAIL_TEMPLATES}
        element={
          <ProtectedRoute>
            <EmailTemplatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.LLM_METRICS}
        element={
          <ProtectedRoute>
            <LlmMetricsPage />
          </ProtectedRoute>
        }
      />

      {/* ── Fallback ───────────────────── */}
      {/* Redirect stub — unmatched paths fall back to the dashboard. */}
      <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
    </Routes>
  );
}
